<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <div class="modal-container glass-modal-container modal-document">
      <div class="modal-header glass-modal-header">
        <h3>Add Document to Dispute #{{ dispute.invoice_id || '' }}</h3>
        <button class="modal-close" @click="close">×</button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label>Document Title *</label>
          <input 
            type="text" 
            v-model="form.title" 
            class="modal-input glass-input"
            placeholder="e.g., Customer Email, Receipt, Contract"
          />
        </div>
        
        <div class="form-group">
          <label>Document Type *</label>
          <select v-model="form.document_type" class="modal-select glass-select">
            <option :value="null">Select document type</option>
            <option value="email">Email</option>
            <option value="receipt">Receipt</option>
            <option value="contract">Contract</option>
            <option value="evidence">Evidence</option>
            <option value="correspondence">Correspondence</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Notes</label>
          <textarea 
            v-model="form.notes" 
            rows="2" 
            class="modal-textarea glass-input"
            placeholder="Add any notes about this document..."
          ></textarea>
        </div>
        
        <div class="form-group">
          <label>Upload Document *</label>
          <input type="file" @change="handleFileUpload" accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx" class="file-input" />
          <p class="file-hint">Accepted formats: PDF, JPG, PNG, GIF, DOC, DOCX (Max 10MB)</p>
          <div v-if="form.file_preview && form.file_preview.type === 'image'" class="image-preview">
            <img :src="form.file_preview.url" alt="Preview" />
          </div>
        </div>
        
        <div v-if="dispute.documents.length" class="existing-documents">
          <label>Existing Documents ({{ dispute.documents.length }})</label>
          <div class="documents-list">
            <div v-for="(doc, idx) in dispute.documents" :key="idx" class="document-item glass-doc-item">
              <div class="doc-info">
                <div class="doc-title">{{ doc.title }}</div>
                <div class="doc-type">{{ doc.document_type }}</div>
              </div>
              <button @click="viewDocument(doc)" class="view-doc-btn glass-btn-small">View</button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer glass-modal-footer">
        <button class="btn-secondary glass-btn" @click="close">Cancel</button>
        <button class="btn-primary glass-btn-primary" @click="submit" :disabled="!canSubmit">Upload Document</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import Requestor from "@/utilities/Requestor"

export default {
  name: 'AddDocumentModal',
  props: {
    visible: { type: Boolean, default: false },
    dispute: { type: Object, default: null }
  },
  emits: ['close', 'added'],
  setup(props, { emit }) {
    const form = ref({
      dispute_id: null,
      title: '',
      document_type: null,
      notes: '',
      file: null,
      file_preview: null
    })
    
    const canSubmit = computed(() => {
      return props.dispute && form.value.title && form.value.document_type && form.value.file
    })
    
    const resetForm = () => {
      form.value = {
        dispute_id: props.dispute.id || null,
        title: '',
        document_type: null,
        notes: '',
        file: null,
        file_preview: null
      }
    }
    
    const handleFileUpload = (event) => {
      const file = event.target.files[0]
      if (file) {
        if (file.size > 10 * 1024 * 1024) {
          // Show toast or alert
          return
        }
        form.value.file = file
        if (file.type.startsWith('image/')) {
          const reader = new FileReader()
          reader.onload = (e) => {
            form.value.file_preview = { url: e.target.result, type: 'image' }
          }
          reader.readAsDataURL(file)
        } else {
          form.value.file_preview = null
        }
      }
    }
    
    const viewDocument = (document) => {
      // Emit event to parent to show document viewer
      emit('view-document', document)
    }
    
    const submit = async () => {
      if (!canSubmit.value) return
      
      const formData = new FormData()
      formData.append("dispute_id", props.dispute.id)
      formData.append("title", form.value.title)
      formData.append("document_type", form.value.document_type)
      formData.append("notes", form.value.notes || "")
      formData.append("document", form.value.file)
      
      try {
        const response = await Requestor.postAsyncP("/api/sales/add_dispute_document/", formData)
        if (response.data.error) {
          console.error(response.data.error)
        } else {
          emit('added')
          close()
        }
      } catch (error) {
        console.error('Error adding document:', error)
      }
    }
    
    const close = () => {
      resetForm()
      emit('close')
    }
    
    watch(() => props.visible, (isVisible) => {
      if (isVisible && props.dispute) {
        form.value.dispute_id = props.dispute.id
      }
    })
    
    return {
      form,
      canSubmit,
      handleFileUpload,
      viewDocument,
      submit,
      close
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  overflow-y: auto;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s ease;
  margin: auto;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 24px 24px 0 0;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #94a3b8;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #475569;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0 0 24px 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.modal-input,
.modal-select,
.modal-textarea {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s;
  color: #1e293b;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 10px;
}

.modal-input:focus,
.modal-select:focus,
.modal-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modal-textarea {
  resize: vertical;
}

.file-input {
  padding: 8px 0;
}

.file-hint {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 6px;
}

.image-preview {
  margin-top: 12px;
}

.image-preview img {
  max-width: 200px;
  max-height: 150px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.existing-documents {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.existing-documents label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 12px;
  display: block;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.document-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
}

.document-item:hover {
  background: rgba(0, 0, 0, 0.06);
}

.doc-info {
  display: flex;
  flex-direction: column;
}

.doc-title {
  font-weight: 500;
  font-size: 13px;
  color: #1e293b;
}

.doc-type {
  font-size: 11px;
  color: #94a3b8;
}

.view-doc-btn {
  padding: 4px 12px;
  font-size: 12px;
  background: rgba(54, 64, 80, 0.85);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn-secondary {
  background: rgba(241, 245, 249, 0.9);
  color: #475569;
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DisputedOrders/components/AddDocumentModal.vue